import React, { useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import CustomButton from "../ui/CustomButton";
import SessionCountdown from "./SessionCountdown";

// Prompt shown when the player has an in-progress game they can jump back into.
// Wording is intentionally soft: our game-window timer does not perfectly align
// with the game's own internal timer, so we never assert the session is still
// live. Continuing re-launches the SAME session (matchId + auth token come back
// from the resume `POST /game` response) with no wallet debit.
const ResumeGameModal = ({
  isOpen,
  onContinue,
  onDismiss,
  isLoading = false,
  lobby,
}) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen || !lobby) return null;

  const endsAt = lobby.userActiveSession?.gameWindowEndsAt ?? null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={isLoading ? undefined : onDismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="resume-game-title"
    >
      <div
        className="w-full max-w-[320px] bg-blackShade2 rounded-2xl shadow-xl p-6 relative border border-white/5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          disabled={isLoading}
          className={`absolute top-4 right-4 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white/70 hover:text-white transition-colors ${
            isLoading ? "opacity-40 cursor-not-allowed pointer-events-none" : ""
          }`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <h2
          id="resume-game-title"
          className="text-lg font-semibold text-white text-center mb-4"
        >
          Resume Game Session
        </h2>

        {endsAt && <SessionCountdown endsAt={endsAt} className="mb-4" />}

        <p className="text-sm text-lightGray text-center mb-6">
          It looks like you have a game in progress. Please complete your open
          match session before it expires.
        </p>

        <CustomButton
          onClick={onContinue}
          disabled={isLoading}
          color="green"
          className="w-full py-2 font-black font-saira tracking-[0.005em] flex justify-center items-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              LOADING
            </>
          ) : (
            "CONTINUE GAME"
          )}
        </CustomButton>

        <button
          type="button"
          onClick={onDismiss}
          disabled={isLoading}
          className={`w-full mt-3 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors ${
            isLoading ? "opacity-40 cursor-not-allowed pointer-events-none" : ""
          }`}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default ResumeGameModal;
